<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- ── DESKTOP HEADER ── -->
      <div class="hidden md:flex items-center justify-between mb-2">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">{{ typeLabelPlural }}</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            <span v-if="loading" class="inline-block w-24 h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></span>
            <span v-else>{{ expenses.length }} {{ type}}{{ expenses.length !== 1 ? 's' : '' }}</span>
          </p>
        </div>
        <button @click="openAdd"
          :class="['flex items-center gap-2 active:scale-95 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm transition', btnClass]">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          Add {{ typeLabel }}
        </button>
      </div>

      <!-- ── MOBILE HEADER ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <div :class="['px-4 pt-3 pb-6', gradientClass]">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">{{ typeLabelPlural }}</h1>
              <p :class="['text-xs mt-0.5', textColorClass]">
                <span v-if="loading">Loading...</span>
                <span v-else>{{ expenses.length }} {{ type}}{{ expenses.length !== 1 ? 's' : '' }}</span>
              </p>
            </div>
            <button @click="openAdd"
              class="flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add
            </button>
          </div>
          <!-- Stats -->
          <div v-if="!loading" class="grid grid-cols-2 gap-2 mb-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p :class="['text-[9px] font-bold uppercase tracking-widest mb-1', textColorClass]">{{ typeLabelPlural }}</p>
              <p class="text-sm font-bold text-white">{{ expenses.length }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-amber-200 uppercase tracking-widest mb-1">Total Amount</p>
              <p class="text-xs font-bold text-white">Rs {{ formatWholeNum(totalAmount) }}</p>
            </div>
          </div>
          <div v-else class="grid grid-cols-2 gap-2 mb-2">
            <div v-for="i in 2" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Compact filter bar -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-2">
          <!-- Search input -->
          <div class="relative mb-2">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
            </svg>
            <input v-model="search" type="text" :placeholder="`Search ${typePlural}...`"
              :class="['w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-10 py-3 text-sm focus:outline-none focus:border-transparent shadow-sm transition', ringClass]" />
            <button v-if="search" @click="search = ''"
              class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="flex items-center gap-2">
            <!-- Payment method pills (left side) -->
            <button v-for="opt in [{v:'',l:'All'},{v:'cash',l:'Cash'},{v:'bank',l:'Bank'}]" :key="opt.v"
              @click="changePaymentMethod(opt.v)"
              :class="['shrink-0 text-xs font-semibold px-3.5 py-2 rounded-full transition', paymentMethod === opt.v ? pillActiveClass : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700']">
              {{ opt.l }}
            </button>

            <div class="flex-1"></div>

            <!-- Filters button (right side) -->
            <button @click="mobileFiltersOpen = true"
              :class="['shrink-0 flex items-center gap-1.5 text-xs font-semibold px-3.5 py-2 rounded-full transition', activePeriod !== 'this_month' ? pillActiveClass : 'bg-white dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700']">
              <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75"/>
              </svg>
              Filters
            </button>
          </div>
        </div>
      </div>

      <!-- Mobile filters bottom sheet -->
      <Teleport to="body">
        <Transition
          enter-active-class="transition-opacity duration-300"
          enter-from-class="opacity-0" enter-to-class="opacity-100"
          leave-active-class="transition-opacity duration-200"
          leave-from-class="opacity-100" leave-to-class="opacity-0">
          <div v-if="mobileFiltersOpen"
            class="md:hidden fixed inset-0 z-40 bg-black/40 backdrop-blur-sm"
            @click="mobileFiltersOpen = false">
          </div>
        </Transition>
        <Transition
          enter-active-class="transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]"
          enter-from-class="translate-y-full" enter-to-class="translate-y-0"
          leave-active-class="transition-transform duration-200 ease-in"
          leave-from-class="translate-y-0" leave-to-class="translate-y-full">
          <div v-if="mobileFiltersOpen"
            class="md:hidden fixed inset-x-0 bottom-0 z-50 flex flex-col bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl max-h-[80vh]"
            @click.stop>
            <!-- Handle -->
            <div class="flex justify-center pt-3 pb-1 shrink-0">
              <div class="w-10 h-1 rounded-full bg-slate-300 dark:bg-slate-600"></div>
            </div>
            <!-- Title + close -->
            <div class="flex items-center justify-between px-4 py-3 border-b border-slate-100 dark:border-slate-800 shrink-0">
              <p class="font-semibold text-slate-800 dark:text-white text-base">Date Range</p>
              <button @click="mobileFiltersOpen = false"
                class="w-8 h-8 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <!-- Content -->
            <div class="overflow-y-auto flex-1 px-4 py-4">
              <div class="flex flex-wrap gap-2">
                <button v-for="opt in periods" :key="opt.value"
                  @click="changePeriod(opt.value); if(opt.value !== 'custom') mobileFiltersOpen = false"
                  :class="['text-xs font-semibold px-3.5 py-2 rounded-full transition', activePeriod === opt.value ? pillActiveClass : 'bg-slate-50 dark:bg-slate-800 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-slate-700']">
                  {{ opt.label }}
                </button>
              </div>
              <!-- Custom date inputs -->
              <div v-if="activePeriod === 'custom'" class="mt-4 space-y-3">
                <div>
                  <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">From Date</label>
                  <input v-model="customStartDate" type="date"
                    class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white rounded-xl px-3 py-2.5 text-sm" />
                </div>
                <div>
                  <label class="block text-xs font-semibold text-slate-600 dark:text-slate-400 mb-1.5">To Date</label>
                  <input v-model="customEndDate" type="date"
                    class="w-full bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white rounded-xl px-3 py-2.5 text-sm" />
                </div>
                <button @click="applyCustomDates(); mobileFiltersOpen = false"
                  :class="['w-full text-white font-semibold py-3 rounded-xl transition', btnClass]">
                  Apply Custom Range
                </button>
              </div>
            </div>
          </div>
        </Transition>
      </Teleport>

      <!-- ── DESKTOP: Filters + Stats ── -->
      <div class="hidden md:flex flex-wrap items-center gap-2 mb-4">
        <!-- Payment method filter -->
        <div class="relative">
          <select v-model="paymentMethod" @change="changePaymentMethod(paymentMethod)"
            :class="['appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-8 py-2.5 text-sm focus:outline-none focus:ring-2 transition cursor-pointer', ringClass]">
            <option value="">All Payments</option>
            <option value="cash">Cash Only</option>
            <option value="bank">Bank Only</option>
          </select>
          <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
        </div>

        <!-- Date filter -->
        <div class="relative">
          <select v-model="activePeriod" @change="changePeriod(activePeriod)"
            :class="['appearance-none bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl pl-3 pr-8 py-2.5 text-sm focus:outline-none focus:ring-2 transition cursor-pointer', ringClass]">
            <option value="today">Today</option>
            <option value="yesterday">Yesterday</option>
            <option value="this_week">This Week</option>
            <option value="last_week">Last Week</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="this_year">This Year</option>
            <option value="last_year">Last Year</option>
            <option value="custom">Custom...</option>
          </select>
          <svg class="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
          </svg>
        </div>

        <!-- Custom date range -->
        <template v-if="activePeriod === 'custom'">
          <input v-model="customStartDate" type="date" @change="applyCustomDates"
            :class="['bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition', ringClass]" />
          <span class="self-center text-slate-400 text-sm">to</span>
          <input v-model="customEndDate" type="date" @change="applyCustomDates"
            :class="['bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 transition', ringClass]" />
        </template>

        <!-- Search -->
        <div class="relative w-56">
          <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
          </svg>
          <input v-model="search" type="text" placeholder="Search..." :disabled="loading"
            :class="['w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white placeholder-slate-400 rounded-xl pl-10 pr-10 py-2.5 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition disabled:opacity-50', ringClass]" />
          <button v-if="search" @click="search = ''"
            class="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition">
            <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Spacer -->
        <div class="flex-1"></div>

        <!-- Stats -->
        <div class="hidden md:flex items-center gap-2">
          <template v-if="loading">
            <div class="h-10 w-32 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
          </template>
          <template v-else>
            <div :class="['rounded-xl px-4 py-2 text-right', statBgClass]">
              <p :class="['text-[10px] font-semibold uppercase tracking-wide', statLabelClass]">Total {{ typeLabel }}</p>
              <p :class="['text-sm font-bold', statTextClass]">Rs {{ formatWholeNum(totalAmount) }}</p>
            </div>
          </template>
        </div>
      </div>

      <!-- ── FLASH ── -->
      <div v-if="$page.props.flash?.notice"
        class="mb-4 flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 text-sm px-4 py-3 rounded-xl">
        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ $page.props.flash.notice }}
      </div>
      <div v-if="$page.props.flash?.alert || errors?.length"
        class="mb-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-xl">
        <p v-if="$page.props.flash?.alert">• {{ $page.props.flash.alert }}</p>
        <p v-for="e in (errors || [])" :key="e">• {{ e }}</p>
      </div>

      <!-- ── SKELETON ── -->
      <template v-if="loading">
        <!-- Desktop -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-6">
            <div v-for="i in 4" :key="i" class="h-2.5 w-16 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 6" :key="i" class="flex items-center gap-6 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="h-9 w-9 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse shrink-0"></div>
            <div class="h-3 w-36 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse flex-1"></div>
            <div class="h-3 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
          </div>
        </div>
        <!-- Mobile -->
        <div class="md:hidden space-y-3">
          <div v-for="i in 5" :key="i"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 p-4 space-y-3">
            <div class="flex items-center justify-between">
              <div class="h-4 w-36 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
            <div class="grid grid-cols-2 gap-2">
              <div v-for="j in 2" :key="j" class="h-14 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- ── EMPTY STATE ── -->
      <div v-else-if="filtered.length === 0"
        class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00.75-.75v-.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>
          </svg>
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">
          {{ search ? `No ${typePlural} match your search` : `No ${typePlural} yet` }}
        </p>
        <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
          {{ search ? 'Try a different keyword' : `Add your first ${type} to get started` }}
        </p>
        <button v-if="!search" @click="openAdd" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">+ Add {{ typeLabel }}</button>
        <button v-else @click="search = ''" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">Clear search</button>
      </div>

      <!-- ── DESKTOP TABLE ── -->
      <div v-else class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-slate-100 dark:border-slate-800">
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-10">#</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">{{ typeLabel }} Name</th>
              <th class="text-right px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Amount</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Notes</th>
              <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Added</th>
              <th class="text-center px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(expense, i) in filtered" :key="expense.id"
              @click="router.visit(`${apiPath}/${expense.id}/histories`)"
              :class="['border-t border-slate-50 dark:border-slate-800 transition cursor-pointer', hoverBgClass]">
              <td class="px-5 py-4 text-slate-400 text-xs">{{ i + 1 }}</td>
              <td class="px-5 py-4">
                <div class="flex items-center gap-2.5">
                  <div :class="['w-8 h-8 rounded-xl flex items-center justify-center shrink-0', iconBgClass]">
                    <svg :class="['w-4 h-4', iconColorClass]" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00.75-.75v-.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>
                    </svg>
                  </div>
                  <span class="font-semibold text-slate-800 dark:text-white">{{ expense.name }}</span>
                </div>
              </td>
              <td class="px-5 py-4 text-right">
                <div :class="['font-semibold', statTextClass]">
                  Rs {{ formatNum(expense.amount || 0) }}
                </div>
              </td>
              <td class="px-5 py-4 text-slate-500 dark:text-slate-400 text-sm max-w-xs truncate">
                {{ expense.notes || '—' }}
              </td>
              <td class="px-5 py-4 text-slate-400 text-xs">{{ expense.created_at }}</td>
              <td class="px-5 py-4 text-center" @click.stop>
                <div class="flex items-center justify-center gap-1">
                  <button @click="openEdit(expense)"
                    class="inline-flex items-center justify-center w-8 h-8 text-slate-500 dark:text-slate-400
                           hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                    </svg>
                  </button>
                  <button @click="openDeleteConfirm(expense)"
                    class="inline-flex items-center justify-center w-8 h-8 text-red-400
                           hover:bg-red-50 dark:hover:bg-red-950 rounded-lg transition">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                    </svg>
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="border-t-2 border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
              <td colspan="2" class="px-5 py-3.5">
                <span class="text-xs font-semibold text-slate-500 dark:text-slate-400">
                  Total · {{ filtered.length }} {{ type }}{{ filtered.length !== 1 ? 's' : '' }}
                </span>
              </td>
              <td :class="['px-5 py-3.5 text-right text-sm font-bold', statTextClass]">
                Rs {{ formatWholeNum(totalAmount) }}
              </td>
              <td colspan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <!-- ── MOBILE CARDS ── -->
      <div v-if="!loading" class="md:hidden space-y-3">
        <div v-for="(expense, i) in filtered" :key="expense.id"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <!-- Card header -->
          <div class="flex items-center justify-between px-4 py-3.5 cursor-pointer" @click="toggleExpand(expense.id)">
            <div class="flex items-center gap-2.5 min-w-0">
              <svg class="w-3.5 h-3.5 text-slate-400 shrink-0 transition-transform duration-200"
                :class="expandedId === expense.id ? 'rotate-90 ' + statLabelClass + '' : ''"
                fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
              </svg>
              <div class="min-w-0">
                <p class="font-semibold text-slate-800 dark:text-white truncate">{{ expense.name }}</p>
                <p class="text-xs text-slate-400 mt-0.5">{{ expense.created_at }}</p>
              </div>
            </div>
            <div class="text-right shrink-0 ml-3">
              <p :class="['text-sm font-bold', statTextClass]">Rs {{ formatNum(expense.amount || 0) }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5">total</p>
            </div>
          </div>

          <!-- Expanded details -->
          <div v-if="expandedId === expense.id"
            :class="['p-4 space-y-3', expandedBgClass]">
            <!-- Info cards -->
            <div v-if="expense.notes" class="bg-white dark:bg-slate-900 rounded-xl border border-slate-100 dark:border-slate-800 p-3">
              <p class="text-[10px] text-slate-400 uppercase tracking-wide mb-1">Notes</p>
              <p class="text-sm text-slate-700 dark:text-slate-300">{{ expense.notes }}</p>
            </div>

            <!-- Action buttons -->
            <div class="grid grid-cols-3 gap-2">
              <button @click.stop="router.visit(`${apiPath}/${expense.id}/histories`)"
                :class="['flex items-center justify-center gap-1.5 text-sm font-semibold bg-white dark:bg-slate-900 border rounded-xl py-2.5 transition active:scale-95', statTextClass, actionBorderClass]">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9 12h3.75M9 15h3.75M9 18h3.75m3 .75H18a2.25 2.25 0 002.25-2.25V6.108c0-1.135-.845-2.098-1.976-2.192a48.424 48.424 0 00-1.123-.08m-5.801 0c-.065.21-.1.433-.1.664 0 .414.336.75.75.75h4.5a.75.75 0 00.75-.75 2.25 2.25 0 00-.1-.664m-5.8 0A2.251 2.251 0 0113.5 2.25H15c1.012 0 1.867.668 2.15 1.586m-5.8 0c-.376.023-.75.05-1.124.08C9.095 4.01 8.25 4.973 8.25 6.108V8.25m0 0H4.875c-.621 0-1.125.504-1.125 1.125v11.25c0 .621.504 1.125 1.125 1.125h9.75c.621 0 1.125-.504 1.125-1.125V9.375c0-.621-.504-1.125-1.125-1.125H8.25zM6.75 12h.008v.008H6.75V12zm0 3h.008v.008H6.75V15zm0 3h.008v.008H6.75V18z"/>
                </svg>
                View
              </button>
              <button @click.stop="openEdit(expense)"
                class="flex items-center justify-center gap-1.5 text-sm font-semibold text-indigo-600 dark:text-indigo-400
                       bg-white dark:bg-slate-900 border border-indigo-200 dark:border-indigo-800 rounded-xl py-2.5 transition active:scale-95">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931z"/>
                </svg>
                Edit
              </button>
              <button @click.stop="openDeleteConfirm(expense)"
                class="flex items-center justify-center gap-1.5 text-sm font-semibold text-red-600 dark:text-red-400
                       bg-white dark:bg-slate-900 border border-red-200 dark:border-red-800 rounded-xl py-2.5 transition active:scale-95">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- ── ADD MODAL ── -->
    <Modal v-if="showAdd" @close="closeAdd">
      <template #title>Add {{ typeLabel }}</template>
      <form @submit.prevent="submitAdd" class="space-y-4">
        <InputField v-model="addForm.name" :label="`${typeLabel} Name`" placeholder="e.g. Office Rent" required />
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Notes (Optional)</label>
          <textarea v-model="addForm.notes" rows="3" :placeholder="`Add any notes about this ${type}...`"
            :class="['w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition resize-none', ringClass]"></textarea>
        </div>
        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeAdd"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="addLoading"
            :class="['flex-1 py-2.5 rounded-xl disabled:opacity-60 text-white text-sm font-semibold transition', btnClass]">
            {{ addLoading ? 'Adding...' : `Add ${typeLabel}` }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── EDIT MODAL ── -->
    <Modal v-if="showEdit" @close="closeEdit">
      <template #title>Edit {{ typeLabel }}</template>
      <form @submit.prevent="submitEdit" class="space-y-4">
        <InputField v-model="editForm.name" :label="`${typeLabel} Name`" placeholder="e.g. Office Rent" required />
        <div>
          <label class="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1.5">Notes (Optional)</label>
          <textarea v-model="editForm.notes" rows="3" :placeholder="`Add any notes about this ${type}...`"
            :class="['w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:border-transparent transition resize-none', ringClass]"></textarea>
        </div>
        <div class="flex gap-3 pt-2">
          <button type="button" @click="closeEdit"
            class="flex-1 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium
                   text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition">
            Cancel
          </button>
          <button type="submit" :disabled="editLoading"
            :class="['flex-1 py-2.5 rounded-xl disabled:opacity-60 text-white text-sm font-semibold transition', btnClass]">
            {{ editLoading ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </form>
    </Modal>

    <!-- ── DELETE CONFIRM ── -->
    <ConfirmDialog
      v-if="showDeleteConfirm"
      :title="`Delete ${typeLabel}?`"
      :message="`Are you sure you want to permanently delete '${deleteTarget?.name}'?`"
      :info-message="`WARNING: This will permanently delete ALL ${typeLabel.toLowerCase()} history entries associated with this ${typeLabel.toLowerCase()}. This action cannot be undone and all data will be lost forever.`"
      confirm-text="Delete Forever"
      loading-text="Deleting..."
      variant="danger"
      @confirm="confirmDelete"
      @cancel="closeDeleteConfirm"
    />

  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useForm, usePage, router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import Modal from '@/components/Modal.vue'
import ConfirmDialog from '@/components/ConfirmDialog.vue'
import InputField from '@/components/InputField.vue'

const page         = usePage()
const errors       = computed(() => page.props.errors)
const businessName = computed(() => page.props.auth?.user?.business_name?.trim() || 'Master Ledger')

// ── Type Detection (Expense vs Income) ──────────────────────
const isIncome = computed(() => page.url.startsWith('/incomes'))
const type = computed(() => isIncome.value ? 'income' : 'expense')
const typePlural = computed(() => isIncome.value ? 'incomes' : 'expenses')
const typeLabel = computed(() => isIncome.value ? 'Income' : 'Expense')
const typeLabelPlural = computed(() => isIncome.value ? 'Incomes' : 'Expenses')
const apiPath = computed(() => `/${typePlural.value}`)
const colorScheme = computed(() => isIncome.value ? 'emerald' : 'rose')

// Dynamic color classes (Tailwind needs full class names)
const btnClass = computed(() => isIncome.value
  ? 'bg-emerald-500 hover:bg-emerald-400 shadow-emerald-500/30'
  : 'bg-rose-500 hover:bg-rose-400 shadow-rose-500/30')
const gradientClass = computed(() => isIncome.value
  ? 'bg-gradient-to-br from-emerald-600 via-emerald-500 to-teal-500'
  : 'bg-gradient-to-br from-rose-600 via-rose-500 to-pink-500')
const textColorClass = computed(() => isIncome.value ? 'text-emerald-200' : 'text-rose-200')
const ringClass = computed(() => isIncome.value ? 'focus:ring-emerald-500' : 'focus:ring-rose-500')
const pillActiveClass = computed(() => isIncome.value
  ? 'bg-emerald-500 text-white shadow-sm'
  : 'bg-rose-500 text-white shadow-sm')
const iconBgClass = computed(() => isIncome.value ? 'bg-emerald-100 dark:bg-emerald-950' : 'bg-rose-100 dark:bg-rose-950')
const iconColorClass = computed(() => isIncome.value ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')
const hoverBgClass = computed(() => isIncome.value ? 'hover:bg-emerald-50 dark:hover:bg-emerald-950/20' : 'hover:bg-rose-50 dark:hover:bg-rose-950/20')
const statBgClass = computed(() => isIncome.value
  ? 'bg-emerald-50 dark:bg-emerald-950/40 border-emerald-100 dark:border-emerald-900'
  : 'bg-rose-50 dark:bg-rose-950/40 border-rose-100 dark:border-rose-900')
const statTextClass = computed(() => isIncome.value ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400')
const statLabelClass = computed(() => isIncome.value ? 'text-emerald-500' : 'text-rose-500')
const expandedBgClass = computed(() => isIncome.value
  ? 'border-t border-emerald-100 dark:border-emerald-900/30 bg-emerald-50/30 dark:bg-emerald-950/10'
  : 'border-t border-rose-100 dark:border-rose-900/30 bg-rose-50/30 dark:bg-rose-950/10')
const actionBorderClass = computed(() => isIncome.value
  ? 'border-emerald-200 dark:border-emerald-800'
  : 'border-rose-200 dark:border-rose-800')

const expenses = ref([])
const loading  = ref(true)
const totalAmount = ref(0)

// ── Date Filters ─────────────────────────────────────────
const activePeriod = ref('this_month')
const customStartDate = ref('')
const customEndDate = ref('')

const periods = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_year', label: 'This Year' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom' },
]

// ── Payment Method Filter ────────────────────────────────
const paymentMethod = ref('')

// ── Search ───────────────────────────────────────────────
const search = ref('')
const filtered = computed(() => {
  const q = search.value.trim().toLowerCase()
  return q ? expenses.value.filter(e => e.name.toLowerCase().includes(q) || e.notes?.toLowerCase().includes(q)) : [...expenses.value]
})

// ── Expand ───────────────────────────────────────────────
const expandedId = ref(null)
function toggleExpand(id) {
  expandedId.value = expandedId.value === id ? null : id
}

// ── Mobile filters ───────────────────────────────────────
const mobileFiltersOpen = ref(false)

// ── Fetch ────────────────────────────────────────────────
async function fetchData() {
  loading.value = true
  try {
    const params = new URLSearchParams({
      period: activePeriod.value,
      start_date: customStartDate.value,
      end_date: customEndDate.value,
      payment_method: paymentMethod.value
    })
    const res  = await fetch(`${apiPath.value}/data?${params}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
    const json = await res.json()
    expenses.value = json[typePlural.value] || []
    totalAmount.value = json.total_amount || 0
  } finally {
    loading.value = false
  }
}

function changePeriod(period) {
  activePeriod.value = period
  if (period !== 'custom') {
    fetchData()
  }
}

function changePaymentMethod(method) {
  paymentMethod.value = method
  fetchData()
}

function applyCustomDates() {
  if (customStartDate.value && customEndDate.value) {
    fetchData()
  }
}

onMounted(() => {
  document.title = `${typeLabelPlural.value} | ${businessName.value}`
  fetchData()
})

// ── Utilities ────────────────────────────────────────────
function formatNum(n) {
  return Number(n).toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatWholeNum(n) {
  return Math.floor(Number(n)).toLocaleString('en-PK')
}

// ── Add ──────────────────────────────────────────────────
const showAdd    = ref(false)
const addLoading = ref(false)
const addForm    = ref({ name: '', notes: '' })

function openAdd()  { showAdd.value = true }
function closeAdd() { showAdd.value = false; addForm.value = { name: '', notes: '' } }

function submitAdd() {
  addLoading.value = true
  const payload = {}
  payload[type.value] = addForm.value
  useForm(payload).post(apiPath.value, {
    onSuccess: () => { closeAdd(); fetchData() },
    onFinish:  () => { addLoading.value = false },
  })
}

// ── Edit ─────────────────────────────────────────────────
const showEdit    = ref(false)
const editLoading = ref(false)
const editTarget  = ref(null)
const editForm    = ref({ name: '', notes: '' })

function openEdit(expense) {
  editTarget.value = expense
  editForm.value   = { name: expense.name, notes: expense.notes || '' }
  showEdit.value   = true
}
function closeEdit() { showEdit.value = false; editTarget.value = null }

function submitEdit() {
  editLoading.value = true
  const payload = {}
  payload[type.value] = editForm.value
  useForm(payload).patch(`${apiPath.value}/${editTarget.value.id}`, {
    onSuccess: () => { closeEdit(); fetchData() },
    onFinish:  () => { editLoading.value = false },
  })
}

// ── Delete ───────────────────────────────────────────────
const showDeleteConfirm = ref(false)
const deleteTarget      = ref(null)

function openDeleteConfirm(expense) { deleteTarget.value = expense; showDeleteConfirm.value = true }
function closeDeleteConfirm()       { showDeleteConfirm.value = false; deleteTarget.value = null }

function confirmDelete() {
  return new Promise((resolve) => {
    router.delete(`${apiPath.value}/${deleteTarget.value.id}`, {
      onSuccess: () => { closeDeleteConfirm(); fetchData(); resolve() },
      onError:   () => { resolve() },
    })
  })
}
</script>
